import React, { useEffect } from 'react';
import { X, ArrowUpRight } from 'lucide-react';
interface Project {
  id: number;
  title: string;
  category: string;
  year: string;
  image: string;
}
interface ProjectModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}
export function ProjectModal({
  project,
  isOpen,
  onClose
}: ProjectModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  if (!isOpen || !project) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center" data-id="element-109">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/95 backdrop-blur-sm transition-opacity duration-300" onClick={onClose} data-id="element-110" />

      {/* Close Button */}
      <button onClick={onClose} className="fixed top-6 right-6 z-[110] p-2 text-white hover:text-yellow-400 transition-colors bg-black/50 rounded-full" data-id="element-111">
        <X size={32} data-id="element-112" />
      </button>

      {/* Modal Content */}
      <div className="relative z-[105] w-full h-full overflow-y-auto pt-24 pb-12 px-4 md:px-6" data-id="element-113">
        <div className="max-w-4xl mx-auto bg-black border border-white/10 min-h-screen animate-fade-in-up" data-id="element-114">
          {/* Header */}
          <div className="p-8 md:p-12 border-b border-white/10" data-id="element-115">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8" data-id="element-116">
              <div data-id="element-117">
                <div className="flex items-center gap-4 mb-4" data-id="element-118">
                  <span className="bg-yellow-400 text-black text-xs font-bold px-3 py-1 uppercase tracking-wider" data-id="element-119">
                    {project.category}
                  </span>
                  <span className="text-gray-400 font-bold text-sm" data-id="element-120">
                    {project.year}
                  </span>
                </div>
                <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter" data-id="element-121">
                  {project.title}
                </h2>
              </div>

              <a href="#" className="flex items-center text-white hover:text-yellow-400 transition-colors font-bold uppercase tracking-wider text-sm group" data-id="element-122">
                Visit Live Site
                <ArrowUpRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" data-id="element-123" />
              </a>
            </div>

            <p className="text-xl text-gray-400 leading-relaxed max-w-2xl" data-id="element-124">
              A comprehensive brand identity and digital experience designed to
              push boundaries. We focused on creating a visual language that
              speaks to the modern consumer while maintaining timeless appeal.
            </p>
          </div>

          {/* Long Vertical Image (Behance Style) */}
          <div className="w-full" data-id="element-125">
            <img src={project.image} alt={project.title} className="w-full h-auto object-cover" data-id="element-126" />
            {/* Mocking additional content images by reusing the same image with different crops/filters or just placeholders */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0" data-id="element-127">
              <img src={project.image} alt="Detail 1" className="w-full h-[600px] object-cover grayscale hover:grayscale-0 transition-all duration-500" data-id="element-128" />
              <img src={project.image} alt="Detail 2" className="w-full h-[600px] object-cover grayscale hover:grayscale-0 transition-all duration-500" data-id="element-129" />
            </div>
            <img src={project.image} alt="Full width detail" className="w-full h-auto object-cover" data-id="element-130" />
          </div>

          {/* Footer of Modal */}
          <div className="p-12 border-t border-white/10 text-center" data-id="element-131">
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-132">
              Ready to start your project?
            </h3>
            <button onClick={onClose} className="inline-block bg-yellow-400 text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-colors" data-id="element-133">
              Close Project
            </button>
          </div>
        </div>
      </div>
    </div>;
}